import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert, Chip, Grid2 as Grid, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxHistopathologyListDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { formatDateTime } from "@/shared/datetime";

export function DiagnosticsHistopathologyPage() {
  const canEnter = useAuth(s => s.hasPermission("diagnostics.histopath.enter"));
  const qc = useQueryClient();
  const [detailId, setDetailId] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["dx-histopath"], queryFn: () => diagnosticsApi.listHistopathology() });

  const [order, setOrder] = useState(""); const [spec, setSpec] = useState(""); const [gross, setGross] = useState(""); const [micro, setMicro] = useState(""); const [dx, setDx] = useState("");
  const create = useMutation({
    meta: { successMessage: "Histopathology report created" },
    mutationFn: () => diagnosticsApi.createHistopathology({ diagnosticOrderId: order, specimenDescription: spec || null, grossFindings: gross || null, microscopicFindings: micro || null, diagnosis: dx || null }),
    onSuccess: () => { setOrder(""); setSpec(""); setGross(""); setMicro(""); setDx(""); qc.invalidateQueries({ queryKey: ["dx-histopath"] }); },
  });

  const columns: GridColDef<DxHistopathologyListDto>[] = [
    { field: "diagnosis", headerName: "Diagnosis", flex: 1, minWidth: 220 },
    { field: "imageCount", headerName: "Images", width: 90, align: "right", headerAlign: "right" },
    { field: "status", headerName: "Status", width: 120, renderCell: p => <Chip size="small" label={p.row.status} color={p.row.status === "Final" ? "success" : "default"} variant="outlined" /> },
    { field: "createdAt", headerName: "Created", width: 165, valueGetter: v => v ? formatDateTime(v as string) : "" },
    { field: "actions", headerName: "", flex: 1, minWidth: 90, sortable: false, align: "right", renderCell: p => <Button size="small" onClick={() => setDetailId(p.row.id)}>Open</Button> },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · Histopathology</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>New report</Typography>
            <Stack spacing={1.5}>
              <DiagnosticOrderIdField value={order} onChange={setOrder} required />
              <TextField label="Specimen description" size="small" value={spec} onChange={e => setSpec(e.target.value)} />
              <TextField label="Gross findings" size="small" multiline minRows={2} value={gross} onChange={e => setGross(e.target.value)} />
              <TextField label="Microscopic findings" size="small" multiline minRows={2} value={micro} onChange={e => setMicro(e.target.value)} />
              <TextField label="Diagnosis" size="small" multiline minRows={2} value={dx} onChange={e => setDx(e.target.value)} />
              {create.isError && <Alert severity="error">{errorMessage(create.error)}</Alert>}
              <Button variant="contained" disabled={!order || !canEnter || create.isPending} onClick={() => create.mutate()}>Create</Button>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Reports</Typography>
            {list.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(list.error)}</Alert>}
            <Box sx={{ height: 460 }}>
              <DataGrid rows={list.data ?? []} columns={columns} loading={list.isLoading} getRowId={r => r.id}
                disableRowSelectionOnClick pageSizeOptions={[25]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <HistopathologyDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

function HistopathologyDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const canEnter = useAuth(s => s.hasPermission("diagnostics.histopath.enter"));
  const canApprove = useAuth(s => s.hasPermission("diagnostics.histopath.approve"));
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["dx-histopath-item", id], queryFn: () => diagnosticsApi.getHistopathology(id!), enabled: !!id });
  const r = query.data;
  const isFinal = r?.status === "Final";
  const refresh = () => { qc.invalidateQueries({ queryKey: ["dx-histopath-item", id] }); qc.invalidateQueries({ queryKey: ["dx-histopath"] }); };

  const [spec, setSpec] = useState(""); const [gross, setGross] = useState(""); const [micro, setMicro] = useState(""); const [dx, setDx] = useState("");
  const [imgUrl, setImgUrl] = useState(""); const [imgCap, setImgCap] = useState(""); const [signature, setSignature] = useState("");
  useEffect(() => { if (r) { setSpec(r.specimenDescription ?? ""); setGross(r.grossFindings ?? ""); setMicro(r.microscopicFindings ?? ""); setDx(r.diagnosis ?? ""); } }, [r]);

  const save = useMutation({ meta: { successMessage: "Saved" }, mutationFn: () => diagnosticsApi.updateHistopathology(id!, { specimenDescription: spec || null, grossFindings: gross || null, microscopicFindings: micro || null, diagnosis: dx || null }), onSuccess: refresh });
  const addImage = useMutation({ meta: { successMessage: "Image added" }, mutationFn: () => diagnosticsApi.addHistopathologyImage(id!, { url: imgUrl.trim(), caption: imgCap.trim() || null }), onSuccess: () => { setImgUrl(""); setImgCap(""); refresh(); } });
  const finalize = useMutation({ meta: { successMessage: "Report finalized" }, mutationFn: () => diagnosticsApi.finalizeHistopathology(id!, { signature }), onSuccess: refresh });

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Histopathology report {r ? `· ${r.status}` : ""}</DialogTitle>
      <DialogContent dividers>
        {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
        {r && (
          <Stack spacing={1.5}>
            <TextField label="Specimen description" size="small" value={spec} onChange={e => setSpec(e.target.value)} disabled={isFinal} />
            <TextField label="Gross findings" size="small" multiline minRows={2} value={gross} onChange={e => setGross(e.target.value)} disabled={isFinal} />
            <TextField label="Microscopic findings" size="small" multiline minRows={2} value={micro} onChange={e => setMicro(e.target.value)} disabled={isFinal} />
            <TextField label="Diagnosis" size="small" multiline minRows={2} value={dx} onChange={e => setDx(e.target.value)} disabled={isFinal} />
            {!isFinal && canEnter && <Button variant="outlined" disabled={save.isPending} onClick={() => save.mutate()} sx={{ alignSelf: "flex-start" }}>Save changes</Button>}

            <Divider />
            <Typography variant="subtitle2">Images</Typography>
            <List dense>{r.images.map((im, i) => <ListItem key={i}><ListItemText primary={im.caption ?? im.url} secondary={im.url} /></ListItem>)}</List>
            {!isFinal && canEnter && (
              <Stack direction="row" spacing={1}>
                <TextField label="Image URL" size="small" value={imgUrl} onChange={e => setImgUrl(e.target.value)} sx={{ flexGrow: 1 }} />
                <TextField label="Caption" size="small" value={imgCap} onChange={e => setImgCap(e.target.value)} sx={{ width: 160 }} />
                <Button variant="outlined" disabled={!imgUrl.trim() || addImage.isPending} onClick={() => addImage.mutate()}>Add</Button>
              </Stack>
            )}

            {!isFinal && canApprove && (
              <>
                <Divider />
                <TextField label="Digital signature" size="small" value={signature} onChange={e => setSignature(e.target.value)} helperText="Required to finalize (diagnosis must be set)" />
                {finalize.isError && <Alert severity="error">{errorMessage(finalize.error)}</Alert>}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!isFinal && canApprove && <Button variant="contained" disabled={!signature || finalize.isPending} onClick={() => finalize.mutate()}>Finalize</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
